import {
  DemandeAffiliation as DemandeAffiliationGql,
  DiplomeEnum,
  MotivationEnum,
  SecteurEmploiEnum,
} from '@graphql/generated';
import { DemandeAffiliation } from '@lib/models/demande-affiliation/demande-affiliation.model';
import { CiviliteMapper } from './civilite-mapper';
import { StatutBackOfficeMapper } from './statut-backoffice-mapper';
import { StatutConsultantMapper } from './statut-consultant-mapper';
import { StatutJuridiqueMapper } from './statut-juridique-mapper';
import { DiplomeMapper } from './diplome-mapper';
import { MotivationMapper } from './motivation-mapper';
import { SecteurEmploiMapper } from './secteur-emploi-mapper';
import { DocumentMapper } from './document-mapper';
import { ParrainMapper } from './parrain-mapper';
import { WarningMapper } from './warning-mapper';

export class DemandeAffiliationMapper {
  public static fromGraphQL(
    input: DemandeAffiliationGql,
    isBackOffice: boolean
  ): DemandeAffiliation {
    return {
      id: input.id,
      publicId: input.publicId,
      codeFilleul: input.codeCom ?? undefined,
      codeParrain: input.codeComParrain,
      parrain: ParrainMapper.fromGraphQL(input.parrain),
      dateUpdate: input.dateUpdate,
      dateDeclaration: input.dateDeclaration,
      statutConsultant: StatutConsultantMapper.fromGraphQl(input.statutConsultant),
      statutBackOffice: StatutBackOfficeMapper.fromGraphQl(input.statutBackOffice),
      gestionnaireId: input.gestionnaireId ?? undefined,
      nomUsage: input.nomUsage!,
      nomNaissance: input.nomNaissance!,
      prenom: input.prenom!,
      prenomsSecondaires: input.prenomsSecondaires ?? undefined,
      civilite: CiviliteMapper.fromGraphQl(input.civilite),
      nationalite: input.nationalite ?? undefined,
      dateNaissance: input.dateNaissance ? new Date(input.dateNaissance) : undefined,
      lieuNaissance: input.lieuNaissance ?? undefined,
      departementNaissance: input.departementNaissance ?? undefined,
      pays: input.pays ?? undefined,
      mobile: input.mobile ?? undefined,
      telephoneBureau: input.telephoneBureau ?? undefined,
      email: input.email ?? undefined,
      emailFacturation: input.emailFacturation ?? undefined,
      diplome: DiplomeMapper.fromGraphQl(input.diplome as DiplomeEnum),
      secteurEmploi: SecteurEmploiMapper.fromGraphQl(
        input.secteurEmploi as SecteurEmploiEnum
      ),
      premiereExperienceEntrepreunariale:
        input.premiereExperienceEntrepreunariale ?? undefined,
      premiereExperienceMarketingRelationnel:
        input.premiereExperienceMarketingRelationnel ?? undefined,
      motivation: MotivationMapper.fromGraphQl(input.motivations as MotivationEnum[]),
      doubleActivite: input.doubleActivite ?? undefined,
      absenceActiviteConcurrente: input.absenceActiviteConcurrente ?? undefined,
      statutJuridique: StatutJuridiqueMapper.fromGraphQl(input.statutJuridique),
      exclusifFinancement: input.exclusifFinancement ?? undefined,
      adresse: input.adresse
        ? {
            voie: input.adresse.voie ?? undefined,
            complementAdresse: input.adresse.complementAdresse ?? undefined,
            codePostal: input.adresse.codePostal ?? undefined,
            ville: input.adresse.ville ?? undefined,
            pays: input.adresse.pays ?? undefined,
          }
        : undefined,
      carteT: input.carteT
        ? {
            numero: input.carteT.numero ?? undefined,
            dateExpiration: input.carteT.dateExpiration ?? undefined,
            cciDelivrance: input.carteT.cciDelivrance ?? undefined,
          }
        : undefined,
      societe: input.societe
        ? {
            numeroImmatriculationRCS: input.societe.numeroImmatriculationRCS ?? undefined,
            raisonSociale: input.societe.raisonSociale ?? undefined,
            formeJuridique: input.societe.formeJuridique ?? undefined,
            numeroSiret: input.societe.numeroSiret ?? undefined,
            numeroTVA: input.societe.numeroTVA ?? undefined,
            adresse: input.societe.adresse
              ? {
                  voie: input.societe.adresse.voie ?? undefined,
                  complementAdresse: input.societe.adresse.complementAdresse ?? undefined,
                  codePostal: input.societe.adresse.codePostal ?? undefined,
                  ville: input.societe.adresse.ville ?? undefined,
                  pays: input.societe.adresse.pays ?? undefined,
                }
              : undefined,
          }
        : undefined,
      professionLiberale: input.professionLiberale
        ? {
            numeroSiren: input.professionLiberale.numeroSiren ?? undefined,
            numeroTVA: input.professionLiberale.numeroTVA ?? undefined,
          }
        : undefined,
      documents:
        input.demandeAffiliationDocuments?.map(d =>
          DocumentMapper.fromGraphQl(d, isBackOffice)!
        ) ?? [],

      gestionnaire: input.gestionnaire ?? undefined,
      warnings:
        input.warnings?.map(w => WarningMapper.fromGraphQl(w.warningTypeAsEnum)!) ?? [],
      refus: input.refus
        ? {
            id: input.refus.id,
            dateRefus: input.refus.dateRefus,
            gestionnare: input.refus.gestionnaireId,
            commentaire: input.refus.commentaire,
          }
        : undefined,
    };
  }
}
